import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Blade from '@/components/ui/Blade/Blade';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs/Tabs';
import { ProjectDtoIn } from '@/models/in/ProjectDtoIn';
import { getProjectStatusColor, getProjectStatusLabelKey } from '@/helpers/projectStatus';
import { ProjectStatus } from '@/models/common/ProjectStatus';
import { usePostAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import ProjectStatusTimeline from './ProjectStatusTimeline';

interface ProjectBladeProps {
  project: ProjectDtoIn;
  onClose: () => void;
  /**
   * The office reviews a project by its exact sub-stage (בדיקה ראשונית / ועדת רווחה / ועדת
   * התמיכות) and opens it to read what the coordinator wrote, so the management screen asks for
   * the exact label and the פרטים נוספים tab (SPR-10302). The coordinator keeps the defaults.
   */
  exactStatus?: boolean;
  defaultTab?: 'status' | 'details';
  /**
   * The coordinator's screen passes this: when the office asked a question, the blade shows it
   * with an answer box, and calls back with the updated project once answered (SPR-10302).
   */
  onAnswered?: (updated: ProjectDtoIn) => void;
}

const formatDate = (value?: string | null): string =>
  value
    ? Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value))
    : '';

const formatAmount = (amount: number): string =>
  Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', minimumFractionDigits: 2 }).format(amount);

/**
 * Detail blade of a project, following the designer's component set: header with the status and
 * total amount, and the tabs פרטים נוספים / סטטוס ומסמכים.
 *
 * The התכתבות tab from the design is deliberately absent: project correspondence has no API
 * (inquiries are opened on transactions, supporteds and support requests only), and an empty
 * tab that cannot send a message would be a dead end rather than a feature.
 */
const ProjectBlade = ({ project, onClose, exactStatus = false, defaultTab = 'status', onAnswered }: ProjectBladeProps) => {
  const { t } = useTranslation();
  const [answer, setAnswer] = useState('');
  const [answerError, setAnswerError] = useState<string | null>(null);

  const { mutateAsync: sendAnswer, isPending: isSendingAnswer } = usePostAuthApi<ProjectDtoIn, { response: string }>(
    `funds/${project.fundId}/projects/${project.id}/response`
  );

  const canAnswer = Boolean(onAnswered) && project.status === ProjectStatus.PendingCoordinatorResponse;

  const handleSendAnswer = async () => {
    if (!answer.trim()) {
      setAnswerError(t('projects.blade.answerRequired'));
      return;
    }
    setAnswerError(null);
    try {
      const updated = await sendAnswer({ response: answer.trim() });
      setAnswer('');
      onAnswered?.(updated);
    } catch (error) {
      console.error('Error answering the office question:', error);
      setAnswerError(t('projects.blade.answerFailed'));
    }
  };

  const activityDomainOf = (supplier: ProjectDtoIn['suppliers'][number]) =>
    supplier.activityDomainName ?? supplier.activityDomainOther ?? '';

  return (
    <Blade
      handleXclick={onClose}
      width="642px"
      headerTop={<span>{t('projects.blade.title', { name: project.name })}</span>}
      headerBottom={
        <div className="flex items-center gap-4 flex-wrap">
          <span className="body-sm-regular">{formatDate(project.createdAt)}</span>
          {/* Filled only by the management endpoint: the office handles every fund's projects. */}
          {project.fundName && (
            // Number first, as the fund picker writes it ("7684 | name").
            <span className="body-sm-regular">{project.fundId} | {project.fundName}</span>
          )}
          <span className="flex items-center gap-2">
            <span
              className="w-[10px] h-[10px] rounded-full shrink-0"
              style={{ backgroundColor: getProjectStatusColor(project.status) }}
            />
            <span className="body-sm-regular">{t(getProjectStatusLabelKey(project.status, !exactStatus))}</span>
          </span>
          <span className="body-sm-semibold">{formatAmount(project.totalExpensesAmount)}</span>
        </div>
      }
    >
      <Tabs defaultValue={defaultTab} style={{ direction: 'rtl' }}>
        <TabsList>
          <TabsTrigger value="status">{t('projects.blade.tabs.statusAndDocuments')}</TabsTrigger>
          <TabsTrigger value="details">{t('projects.blade.tabs.additionalInfo')}</TabsTrigger>
        </TabsList>

        <TabsContent value="status">
          <div className="flex flex-col gap-6 p-4">
            {canAnswer && (
              <section className="flex flex-col gap-3 rounded-lg border border-(--border-secondary-strong) p-4">
                <h4 className="body-lr-semibold text-(--font-primary)">{t('projects.blade.answerTitle')}</h4>
                <p className="body-md-regular whitespace-pre-line bg-(--surface-secondary) rounded-lg px-3 py-2">
                  {project.statusComment}
                </p>
                <label htmlFor="project-answer" className="body-sm-semibold text-(--font-secondary)">
                  {t('projects.blade.answerLabel')}
                </label>
                <textarea
                  id="project-answer"
                  rows={4}
                  className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular resize-none"
                  value={answer}
                  onChange={(e) => {
                    setAnswer(e.target.value);
                    if (answerError) setAnswerError(null);
                  }}
                />
                {answerError && <p className="text-red-500 body-sm-regular">{answerError}</p>}
                <div>
                  <Button variant="primary" onClick={handleSendAnswer} disabled={isSendingAnswer}>
                    {isSendingAnswer ? t('saving') : t('projects.blade.answerSubmit')}
                  </Button>
                </div>
              </section>
            )}

            <ProjectStatusTimeline project={project} />

            <section className="flex flex-col gap-3">
              <h4 className="body-lr-semibold text-(--font-primary)">{t('projects.blade.documentsTitle')}</h4>
              {project.documentIds.length === 0 ? (
                <p className="body-sm-regular text-(--font-secondary)">—</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="text-start">
                      <th className="body-sm-semibold text-(--font-secondary) text-start pb-2">
                        {t('projects.blade.documentColumn')}
                      </th>
                      <th className="body-sm-semibold text-(--font-secondary) text-start pb-2">
                        {t('projects.blade.uploadDateColumn')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.documentIds.map((documentId) => (
                      <tr key={documentId}>
                        <td className="body-sm-regular py-1">#{documentId}</td>
                        <td className="body-sm-regular py-1 text-(--font-secondary)">
                          {formatDate(project.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        </TabsContent>

        <TabsContent value="details">
          <div className="flex flex-col gap-6 p-4">
            <section className="flex flex-col gap-1">
              <h4 className="body-sm-semibold text-(--font-secondary)">{t('projects.blade.projectTypeLabel')}</h4>
              <p className="body-md-regular">{project.projectTypeName ?? '—'}</p>
            </section>

            <section className="flex flex-col gap-1">
              <h4 className="body-sm-semibold text-(--font-secondary)">{t('projects.blade.descriptionLabel')}</h4>
              <p className="body-md-regular whitespace-pre-line">{project.description ?? '—'}</p>
            </section>

            <section className="flex flex-col gap-3">
              <h4 className="body-lr-semibold text-(--font-primary)">{t('projects.blade.expensesTitle')}</h4>
              {project.expenses.length === 0 ? (
                <p className="body-sm-regular text-(--font-secondary)">—</p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {project.expenses.map((expense) => (
                    // Two columns, not four: the blade is 642px wide, so four columns squeezed
                    // each label/value pair into ~140px and wrapped the amounts.
                    <li
                      key={expense.id}
                      className="grid grid-cols-2 gap-3 border border-(--border-main) rounded-lg p-3"
                    >
                      <div className="flex flex-col">
                        <span className="body-xs-regular text-(--font-secondary)">
                          {t('projects.wizard.step2.amountLabel')}
                        </span>
                        <span className="body-sm-semibold">{formatAmount(expense.amount)}</span>
                      </div>
                      {/* The form stopped asking for a reason (HAT-1105, 22/10/2026); rows written
                          before that keep theirs, newer ones have none, so the cell only appears
                          when there is something to show - not as a column of dashes. */}
                      {expense.reason && (
                        <div className="flex flex-col">
                          <span className="body-xs-regular text-(--font-secondary)">
                            {t('projects.wizard.step2.reasonLabel')}
                          </span>
                          <span className="body-sm-regular">{expense.reason}</span>
                        </div>
                      )}
                      <div className="flex flex-col">
                        <span className="body-xs-regular text-(--font-secondary)">
                          {t('projects.wizard.step2.frequencyLabel')}
                        </span>
                        <span className="body-sm-regular">{t(`projects.frequency.${expense.frequency}`)}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="body-xs-regular text-(--font-secondary)">
                          {t('projects.wizard.step2.validUntilLabel')}
                        </span>
                        <span className="body-sm-regular">{formatDate(expense.validUntil) || '—'}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="flex flex-col gap-3">
              <h4 className="body-lr-semibold text-(--font-primary)">{t('projects.blade.suppliersTitle')}</h4>
              {project.suppliers.length === 0 ? (
                <p className="body-sm-regular text-(--font-secondary)">{t('projects.blade.noSuppliers')}</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {project.suppliers.map((supplier) => (
                    <li key={supplier.id} className="flex items-center justify-between gap-3">
                      <span className="body-sm-regular">
                        {supplier.name}
                        {activityDomainOf(supplier) && (
                          <span className="text-(--font-secondary)"> · {activityDomainOf(supplier)}</span>
                        )}
                      </span>
                      <span className="body-xs-regular text-(--font-secondary)">
                        {t(`suppliersApproval.approvalStatus.${supplier.approvalStatus}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="flex flex-col gap-1">
              <h4 className="body-sm-semibold text-(--font-secondary)">{t('projects.blade.commentsTitle')}</h4>
              <p className="body-md-regular whitespace-pre-line">{project.userComments ?? '—'}</p>
            </section>
          </div>
        </TabsContent>
      </Tabs>
    </Blade>
  );
};

export default ProjectBlade;
